import React, { useState, useEffect } from 'react';
import { DocumentViewer } from '@/components/installations/document-viewer';
import { FileData } from '@/components/installations/document-upload';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { FileCheck, Trash2, RefreshCw } from 'lucide-react';
import { Card } from '@/components/ui/card';

export default function DocumentTestPage() {
  const [files, setFiles] = useState<FileData[]>([
    {
      id: 'test-document.txt',
      name: 'test-document.txt',
      data: '/api/files/test-document.txt',
      type: 'text/plain',
      size: 24,
    },
    {
      id: 'test-image.svg',
      name: 'test-image.svg',
      data: '/api/files/test-image.svg',
      type: 'image/svg+xml',
      size: 145,
    },
  ]);

  const [dbFiles, setDbFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Load all document files from the database
  const loadDatabaseFiles = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/test/list-documents');
      const result = await response.json();

      if (result.success) {
        setDbFiles(result.documents);
        toast({
          title: 'Success',
          description: `Loaded ${result.documents.length} documents from database`,
        });
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to load documents',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred while loading documents',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Load database files on component mount
  useEffect(() => {
    loadDatabaseFiles();
  }, []);

  const createDatabaseRecords = async () => {
    setLoading(true);
    try {
      // Add test documents to the database
      const response = await fetch('/api/test/create-test-documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documents: [
            {
              filename: 'test-document.txt',
              originalFilename: 'test-document.txt',
              filePath: '~/uploads/documents/dnoDocuments/test-document.txt',
              fileSize: 1024,
              mimeType: 'text/plain',
              category: 'dnoDocuments',
            },
            {
              filename: 'test-image.svg',
              originalFilename: 'test-image.svg',
              filePath: '~/uploads/documents/dnoDocuments/test-image.svg',
              fileSize: 2048,
              mimeType: 'image/svg+xml',
              category: 'dnoDocuments',
            },
          ],
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: 'Success',
          description: 'Test documents created in database',
        });

        // Update state with database IDs
        setFiles((prev) => [
          {
            ...prev[0],
            id: result.documents[0].id,
            data: `/api/documents/${result.documents[0].id}`,
          },
          {
            ...prev[1],
            id: result.documents[1].id,
            data: `/api/documents/${result.documents[1].id}`,
          },
        ]);

        // Reload database files
        loadDatabaseFiles();
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to create test documents',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error creating test documents:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteDocument = async (id: number) => {
    if (!window.confirm(`Are you sure you want to delete document #${id}?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/documents/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: `Document #${id} deleted successfully`,
        });

        // Reload database files
        loadDatabaseFiles();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.message || `Failed to delete document #${id}`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error(`Error deleting document #${id}:`, error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred during deletion',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-8">
      <h1 className="text-2xl font-bold mb-6">Document Viewer Test</h1>

      <div className="mb-8">
        <p className="mb-4 text-muted-foreground">
          This page tests the document viewing functionality with test files stored in the
          filesystem.
        </p>

        <div className="flex flex-wrap gap-4 mb-6">
          <Button onClick={createDatabaseRecords} disabled={loading}>
            <FileCheck className="mr-2 h-4 w-4" />
            Create Test Documents
          </Button>

          <Button onClick={loadDatabaseFiles} variant="outline" disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh Documents
          </Button>
        </div>
      </div>

      <div className="mb-8">
        <DocumentViewer
          documents={files}
          title="Test Documents"
          emptyMessage="No test documents available"
        />
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">All Documents in Database</h2>

        {dbFiles.length === 0 ? (
          <p className="text-muted-foreground">No documents found in database.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {dbFiles.map((file) => (
              <Card key={file.id} className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-medium text-sm">{file.originalFilename}</h3>
                    <p className="text-xs text-muted-foreground">ID: {file.id}</p>
                  </div>
                  <Button
                    variant="destructive"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => deleteDocument(file.id)}
                    disabled={loading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="text-xs">
                  <p>
                    <span className="font-medium">Type:</span> {file.mimeType}
                  </p>
                  <p>
                    <span className="font-medium">Size:</span> {file.fileSize} bytes
                  </p>
                  <p>
                    <span className="font-medium">Path:</span> {file.filePath}
                  </p>
                  <p>
                    <span className="font-medium">Category:</span> {file.category}
                  </p>
                  <p>
                    <span className="font-medium">File Status:</span>{' '}
                    <span
                      className={
                        file.fileExists
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }
                    >
                      {file.fileExists ? 'Exists on disk' : 'Missing from disk'}
                    </span>
                  </p>
                </div>
                <div className="mt-3 flex gap-2">
                  <a
                    href={`/document/${file.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                  >
                    View in PDF Viewer ↗
                  </a>
                  <span className="text-xs text-muted-foreground">|</span>
                  <a
                    href={`/api/documents/${file.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                  >
                    Direct Download ↗
                  </a>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
